'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { registerSchema, type RegisterFormData } from '@/schemas/auth.schema';
import { registrarContador } from '@/services/auth.service';
import { formatCpfCnpj, formatPhone, unformat, unformatCpfCnpj } from '@/lib/masks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: '',
      email: '',
      cpf_cnpj: '',
      telefone: '',
      senha: '',
      confirmacao_senha: '',
    },
  });

  async function onSubmit(data: RegisterFormData) {
    try {
      setIsSubmitting(true);

      const { confirmacao_senha: _, ...rest } = data;
      const payload = {
        ...rest,
        cpf_cnpj: unformatCpfCnpj(data.cpf_cnpj),
        telefone: unformat(data.telefone),
        inativo: false,
      };

      const response = await registrarContador(payload);

      if (response.sucesso) {
        toast.success(
          'Registro realizado com sucesso! Verifique seu email para obter o código de verificação.'
        );
        router.push(`/verificar-conta?email=${encodeURIComponent(data.email)}`);
      } else {
        toast.error(
          response.mensagem || 'Erro ao realizar o registro. Tente novamente.'
        );
      }
    } catch (err: any) {
      toast.error(
        err?.message ||
          'Erro ao conectar com o servidor. Verifique sua conexão e tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="text-center text-sm text-muted-foreground">
        <p>Preencha os dados para registro</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="nome">Nome</Label>
        <Input
          id="nome"
          type="text"
          placeholder="Seu nome completo"
          autoComplete="name"
          {...register('nome')}
        />
        {errors.nome && (
          <p className="text-xs text-destructive">{errors.nome.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="reg-email">Email</Label>
        <Input
          id="reg-email"
          type="email"
          placeholder="seu@email.com"
          autoComplete="email"
          {...register('email')}
        />
        {errors.email && (
          <p className="text-xs text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
        <Input
          id="cpf_cnpj"
          type="text"
          placeholder="00.000.000/0000-00"
          autoComplete="off"
          maxLength={18}
          {...register('cpf_cnpj', {
            onChange: (e) => {
              const formatted = formatCpfCnpj(e.target.value);
              e.target.value = formatted;
              setValue('cpf_cnpj', formatted);
            },
          })}
        />
        {errors.cpf_cnpj && (
          <p className="text-xs text-destructive">{errors.cpf_cnpj.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="telefone">Telefone</Label>
        <Input
          id="telefone"
          type="text"
          placeholder="(00) 00000-0000"
          autoComplete="tel"
          maxLength={15}
          {...register('telefone', {
            onChange: (e) => {
              const formatted = formatPhone(e.target.value);
              e.target.value = formatted;
              setValue('telefone', formatted);
            },
          })}
        />
        {errors.telefone && (
          <p className="text-xs text-destructive">{errors.telefone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="reg-senha">Senha</Label>
        <div className="relative">
          <Input
            id="reg-senha"
            type={showPassword ? 'text' : 'password'}
            placeholder="Mínimo 6 caracteres"
            autoComplete="new-password"
            className="pr-10"
            {...register('senha')}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute right-1 top-1/2 -translate-y-1/2"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {errors.senha && (
          <p className="text-xs text-destructive">{errors.senha.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="reg-confirmacao-senha">Confirmar Senha</Label>
        <div className="relative">
          <Input
            id="reg-confirmacao-senha"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Repita a senha"
            autoComplete="new-password"
            className="pr-10"
            {...register('confirmacao_senha')}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute right-1 top-1/2 -translate-y-1/2"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        {errors.confirmacao_senha && (
          <p className="text-xs text-destructive">{errors.confirmacao_senha.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Registrar
      </Button>

      <div className="text-center text-sm">
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="font-medium text-primary hover:underline"
        >
          Voltar para login
        </button>
      </div>
    </form>
  );
}
